import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  TextField,
  IconButton,
  Button,
  Divider,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { axiosInstance as axios, endpoint } from "../../api/axios";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { Link } from "react-router-dom";
import BottomNavigationBar from "../navbar/BottomNavigationBar";
import ReceiveDetailDialog from "../receive/dialog/ReceiveDetailDialog";

const ReceiveList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openReceiveDetail, setOpenReceiveDetail] = useState(false);

  const [selectedItemDetail, setSelectedItemDetail] = useState(null);
  const [selectedItemHeader, setSelectedItemHeader] = useState(null);

  const handleCloseReceiveDetail = () => {
    setOpenReceiveDetail(false);
  };

  const handleOpenReceiveDetail = async (id) => {
    try {
      const response = await axios.post(
        endpoint.Receive.ReceiveDetailList,
        { id: id },
        {
          headers: {
            Authorization: `${user.token_type} ${user.token}`,
          },
        }
      );
      if (response.data != null) {
        setSelectedItemHeader(response.data?.data);
        setSelectedItemDetail(response.data?.data?.item_transfer_details);
        setOpenReceiveDetail(true);
      }
    } catch (error) {
      console.error("Gagal mengambil detail receive:", error);
    }
  };

  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");

  const toggleSearch = () => {
    setShowSearch(!showSearch);
    setSearchText("");
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const [dataReceive, setDataReceive] = useState([]);
  const [rawData, setRawData] = useState([]);
  const user = useSelector(
    (state) => state.user.users[state.user.currentUserId]
  );

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  const filters = useMemo(
    () => ({
      page: currentPage,
      pageSize: pageSize,
    }),
    [currentPage, pageSize]
  );

  const headers = useMemo(() => {
    if (!user?.token) return {};
    return {
      Authorization: `${user.token_type} ${user.token}`,
    };
  }, [user?.token, user?.token_type]);

  // Memoized and stable fetch function
  const getDataReceive = useCallback(async () => {
    if (!user?.token) return;

    try {
      const response = await axios.post(endpoint.Receive.ReceiveList, filters, {
        headers,
      });
      const data = response.data.data?.data;
      setDataReceive(data || []);
      setRawData(data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Data gagal dieksekusi", error);
    }
  }, [endpoint.Receive.ReceiveList, filters, headers, user?.token]);

  useEffect(() => {
    const handler = setTimeout(() => {
      getDataReceive();
    }, 100); // Delay 100ms

    return () => clearTimeout(handler);
  }, [getDataReceive]);

  // Filter data berdasarkan searchText
  useEffect(() => {
    if (!searchText) {
      setDataReceive(rawData); // tanpa filter
    } else {
      const filtered = rawData.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setDataReceive(filtered);
    }
  }, [searchText, rawData]);

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 30,
          width: "100%",
          textAlign: "center",
          zIndex: 10,
        }}
      >
        {/* Icon untuk menampilkan dan menyembunyikan TextField */}
        <IconButton
          sx={{
            position: "absolute",
            top: -10,
            right: 20,
            width: 30,
            height: 30,
            backgroundColor: "success.light",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            cursor: "pointer",
          }}
          onClick={toggleSearch}
        >
          <SearchIcon sx={{ color: "white" }} />
        </IconButton>

        <Typography
          variant={isMobile ? "p" : "h6"}
          sx={{
            fontWeight: "bold",
            fontFamily: '"Arial", Times, serif',
            color: "white",
            textShadow: "0 0 6px rgba(0,0,0,0.5)",
          }}
        >
          Receive
        </Typography>

        {/* Menampilkan TextField jika showSearch true */}
        {showSearch && (
          <Paper
            elevation={3}
            sx={{ p: 2, mt: 2, display: "flex", alignItems: "center" }}
          >
            <TextField
              label="Cari Receive"
              autoComplete="Off"
              value={searchText}
              onChange={handleSearchChange}
              variant="outlined"
              fullWidth
              sx={{ mr: 2 }}
            />
            {/* Tombol untuk menyembunyikan TextField */}
            <IconButton onClick={toggleSearch}>
              <Typography variant="body1" color="primary">
                <CloseIcon />
              </Typography>
            </IconButton>
          </Paper>
        )}
      </Box>

      <Box
        sx={{
          minHeight: "5vh",
          backgroundImage: "url('/background.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: isMobile ? 2 : 4,
        }}
      ></Box>
      <Box sx={{ mt: -2 }}>
        <Paper
          elevation={3}
          sx={{ p: 2, backgroundColor: "rgba(255, 255, 255, 0.9)" }}
        >
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              {dataReceive.length === 0 ? (
                
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                    minWidth: 300,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Tidak ada data ditemukan.
                  </Typography>
                </Paper>

              ) : (
                dataReceive.map((item) => (
                  <Paper
                    key={item.id}
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                      cursor: "pointer",
                    }}
                    onClick={() => handleOpenReceiveDetail(item.id)}
                  >
                    <Box display="flex" flexDirection="column" gap={4}>
                      <Box display="flex" alignItems="center" gap={3}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: "#1976d2",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Inventory2OutlinedIcon
                            sx={{ color: "white", fontSize: 20 }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Transfer Code
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {item?.transfer_code || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <ReceiveDetailDialog
        data={selectedItemDetail}
        dataHeader={selectedItemHeader}
        openReceiveDetail={openReceiveDetail}
        handleCloseReceiveDetail={handleCloseReceiveDetail}
      />

      <BottomNavigationBar />
    </>
  );
};

export default ReceiveList;
